import { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DOC_TYPES } from "@/lib/scanOptions";
import { ScanItem } from "@/components/scan/ScanItem";
import { ScanWizard } from "@/components/scan/ScanWizard";
import { LinkSelect, linkPayload } from "@/components/scan/LinkSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const norm = (s) => s.replace(/[İIı]/g, "i").toLocaleLowerCase("tr");

const RelinkDialog = ({ scan, onClose, onSaved }) => {
  const [link, setLink] = useState({});
  useEffect(() => { if (scan) setLink({ customer_id: scan.customer_id, portfolio_id: scan.portfolio_id }); }, [scan]);
  const save = async () => {
    try { await api.put(`/scans/${scan.id}/link`, linkPayload(link)); toast.success("İlişki güncellendi"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!scan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Belgeyi ilişkilendir</DialogTitle></DialogHeader>
        <LinkSelect value={link} onChange={setLink} />
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>İptal</Button><Button data-testid="relink-save-btn" onClick={save}>Kaydet</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export default function ScansPage() {
  const [items, setItems] = useState([]);
  const [names, setNames] = useState({});
  const [q, setQ] = useState("");
  const [type, setType] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [relink, setRelink] = useState(null);

  const load = () => api.get("/scans").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    Promise.all([api.get("/customers"), api.get("/portfolios")]).then(([c, p]) => setNames({
      ...Object.fromEntries(c.data.map((x) => [x.id, `Müşteri: ${x.full_name}`])),
      ...Object.fromEntries(p.data.map((x) => [x.id, `Portföy: ${x.title}`])),
    })).catch(() => {});
  }, []);

  const label = (s) => names[s.customer_id] || names[s.portfolio_id] || "";
  const shown = useMemo(() => items.filter((s) => (type === "ALL" || s.doc_type === type)
    && norm(`${s.title} ${label(s)}`).includes(norm(q))), [items, type, q, names]);

  return (
    <div className="space-y-6" data-testid="scans-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Belge Tarama</h1>
          <p className="text-slate-500 text-sm mt-1">Fotoğraftan köşeleri otomatik bulun, kırpın, gölgeleri temizleyin ve PDF olarak saklayın.</p>
        </div>
        <Button data-testid="new-scan-btn" className="bg-slate-900" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" />Yeni tarama</Button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-56"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" /><Input data-testid="scan-search" className="pl-9" placeholder="Belge, müşteri veya portföy ara" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="scan-type-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Tüm türler</SelectItem>{Object.entries(DOC_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {shown.length === 0 ? <Card className="p-10 text-center text-sm text-slate-500">Belge bulunamadı</Card>
        : <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{shown.map((s) => <ScanItem key={s.id} s={s} linkLabel={label(s)} onChanged={load} onRelink={setRelink} />)}</div>}
      <ScanWizard open={open} onClose={() => setOpen(false)} onSaved={load} />
      <RelinkDialog scan={relink} onClose={() => setRelink(null)} onSaved={load} />
    </div>
  );
}
